import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router'

import httpClient from '../httpClient'
import { apiErrorMessage } from '../lib/utils'

import Loading from '../components/common/Loading'
import OrderSummary from '../components/LabOrderSummary/OrderSummary'
import OrderCheckout from '../components/LabOrderSummary/OrderCheckout'
import { useConfirmationModal } from '../hooks'
import { useProductSelectionStore } from '../stores'

const LabOrderSummaryPage = () => {
  const { id } = useParams()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(false)
  const {Modal, setIsOpen} = useConfirmationModal()
  const setCartCount = useProductSelectionStore((state)=> state.setCartCount)

  const hadnleClearCart = useCallback(async()=>{
    setLoading(true)

    try {
      await httpClient.delete(`lab_orders/${id}/clear_cart`)
      const updatedOrder = await httpClient(`lab_orders/${id}`)

      setOrder(updatedOrder)
      setCartCount(0)
    } catch (error) {
      setApiError(apiErrorMessage(error))
    }finally{
      setLoading(false)
    }
  }, [id, setCartCount])

  useEffect(() => {
    (async () => {
      setLoading(true)
      try {
        const data = await httpClient(`lab_orders/${id}`)
        setOrder(data)
        setLoading(false)
      } catch (error) {
        setApiError(apiErrorMessage(error))
        setLoading(false)
      }
    })()
  }, [id])

  const handleUpdateQuantity = useCallback(
    async (guid, quantity) => {
      try {
        await httpClient.patch(`line_items/${guid}/update_quantity`, {
          line_items: [{ quantity }],
        })
        const res = await httpClient(`lab_orders/${id}`)
        setOrder(res)
      } catch (error) {
        setApiError(apiErrorMessage(error))
      }
    },
    [id],
  )

  const handleUpdateMultiOpeningQty = useCallback(
    async (guid, quantity) => {
      try {
        await httpClient.patch(`lab_orders/${id}/multi_openings_line_items/${guid}/update_quantity`, {
          line_items: [{ quantity }],
        })
        const res = await httpClient(`lab_orders/${id}`)
        setOrder(res)
      } catch (error) {
        setApiError(apiErrorMessage(error))
      }
    },
    [id],
  )

  const handleDelete = useCallback(
    async (guid) => {
      setLoading(true)
      try {
        await httpClient.delete(`line_items/${guid}`)
        const res = await httpClient(`lab_orders/${id}`)
        setOrder(res)
        setLoading(false)
      } catch (error) {
        setApiError(apiErrorMessage(error))
        setLoading(false)
      }
    },
    [order],
  )

  const handleDeleteAlbum = useCallback(
    async (albumGuid) => {
      if (!albumGuid) return
      setLoading(true)
      try {
        await httpClient.delete(`lab_orders/${id}/albums/${albumGuid}`)
        const res = await httpClient(`lab_orders/${id}`)
        setOrder(res)
        setLoading(false)
      } catch (error) {
        setApiError(apiErrorMessage(error))
        setLoading(false)
      }
    },
    [order, id],
  )

  const handleDeleteMultiOpening = useCallback(
    async (guid) => {
      if (!guid) return
      setLoading(true)
      try {
        await httpClient.delete(`lab_orders/${id}/multi_openings_line_items/${guid}`)
        const res = await httpClient(`lab_orders/${id}`)
        setOrder(res)
        setLoading(false)
      } catch (error) {
        setApiError(apiErrorMessage(error))
        setLoading(false)
      }
    },
    [order, id],
  )

  if (loading) {
    return <Loading message="Loading order" />
  }

  if (!id || apiError || !order) {
    return null
  }

  return (
    <div className="container-order-review">
      <div className="w-layout-grid grid-67">
        <OrderSummary
          lineItems={order?.included}
          onUpdateQty={handleUpdateQuantity}
          onUpdateMultiOpeningQty={handleUpdateMultiOpeningQty}
          onDelete={handleDelete}
          onDeleteAlbum={handleDeleteAlbum}
          onDeleteMultiOpening={handleDeleteMultiOpening}
          onClearCart={()=>setIsOpen(true)}
        />
        <OrderCheckout order={order?.data} cartCount={order?.included?.length ?? 0} />
      </div>
      <Modal
        message={
          <span
            style={{
              display: 'block',
              maxWidth: '500px',
              padding: '0 36px',
              boxSizing: 'border-box',
              overflowWrap: 'break-word'
            }}
          >
            Are you sure you want to clear your entire cart? This action cannot be undo.
          </span>

        }
        onContinue={() => {
          setIsOpen(false)
          hadnleClearCart()
        }}
      />
    </div>
  )
}

export default LabOrderSummaryPage
